import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { creditsService, CreditBalance, CreditPack } from '../services/CreditsService';

export type CreditState = 'normal' | 'low' | 'empty';

export function getCreditState(available: number): CreditState {
  if (available <= 0) return 'empty';
  if (available < 10) return 'low';
  return 'normal';
}

export interface UseCreditsReturn {
  balance: CreditBalance;
  creditState: CreditState;
  loading: boolean;
  error: string | null;
  packs: CreditPack[];
  packsLoading: boolean;
  purchaseCredits: (packId: string) => Promise<void>;
  purchasing: boolean;
  refresh: () => Promise<void>;
}

export function useCredits(): UseCreditsReturn {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [balance, setBalance] = useState<CreditBalance>({
    available: 0,
    used: 0,
    purchased: 0,
    monthlyAllocation: 0,
  });
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [packsLoading, setPacksLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  const loadBalance = useCallback(async (forceRefresh = false) => {
    if (!currentUser) {
      setBalance({ available: 0, used: 0, purchased: 0, monthlyAllocation: 0 });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await creditsService.getBalance(forceRefresh);
      setBalance(data);
    } catch (err) {
      console.error('Failed to load credits:', err);
      setError('Failed to load credit balance');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadPacks = useCallback(async () => {
    try {
      setPacksLoading(true);
      const data = await creditsService.getCreditPacks();
      setPacks(data);
    } catch (err) {
      console.error('Failed to load credit packs:', err);
    } finally {
      setPacksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalance();
    loadPacks();
  }, [loadBalance, loadPacks]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      creditsService.invalidateCache();
      loadBalance(true);
      const newUrl = location.pathname + '?tab=credits';
      window.history.replaceState({}, '', newUrl);
    }
  }, [location, loadBalance]);

  const purchaseCredits = useCallback(async (packId: string) => {
    if (!currentUser) {
      setError('You must be logged in to purchase credits');
      return;
    }

    setPurchasing(true);
    setError(null);

    try {
      const result = await creditsService.purchaseCredits(packId);
      if (!result.success && !result.checkoutUrl) {
        setError(result.error || 'Failed to initiate purchase');
      }
    } catch (err) {
      console.error('Purchase failed:', err);
      setError('Failed to initiate purchase');
    } finally {
      setPurchasing(false);
    }
  }, [currentUser]);

  const creditState = getCreditState(balance.available);

  return {
    balance,
    creditState,
    loading,
    error,
    packs,
    packsLoading,
    purchaseCredits,
    purchasing,
    refresh: () => loadBalance(true),
  };
}

export type { CreditBalance, CreditPack };
